import { useState, useEffect, useCallback } from 'react';
import { X, History, RefreshCw, RotateCcw, GitBranch, Eye, ChevronRight, Clock, Check } from 'lucide-react';
import { API_URL } from '../config';

interface Version {
  id: string;
  version_number: number;
  hash: string;
  message: string;
  auto_save: boolean;
  created_at: string;
  diff_summary?: string;
  node_count: number;
  edge_count: number;
}

interface VersioningPanelProps {
  isOpen: boolean;
  onClose: () => void;
  flowId: string;
  flowName: string;
  currentNodes: any[];
  currentEdges: any[];
  onRollback: (nodes: any[], edges: any[]) => void;
  userId?: string;
}

export default function VersioningPanel({
  isOpen,
  onClose,
  flowId,
  flowName,
  currentNodes,
  currentEdges,
  onRollback,
  userId = 'default',
}: VersioningPanelProps) {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  const [versionDetails, setVersionDetails] = useState<any | null>(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [compareVersion, setCompareVersion] = useState<Version | null>(null);
  const [diffResult, setDiffResult] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const loadVersions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/versions/${flowId}/list?limit=50`);
      if (res.ok) {
        const data = await res.json();
        setVersions(data.versions || []);
      }
    } catch (e) {
      console.error('Error loading versions:', e);
    } finally {
      setLoading(false);
    }
  }, [flowId]);

  const loadVersionDetails = async (versionId: string) => {
    try {
      const res = await fetch(`${API_URL}/versions/${flowId}/${versionId}`);
      if (res.ok) {
        const data = await res.json();
        setVersionDetails(data);
      }
    } catch (e) {
      console.error('Error loading version details:', e);
    }
  };

  useEffect(() => {
    if (isOpen && flowId) {
      loadVersions();
    }
  }, [isOpen, flowId, loadVersions]);

  useEffect(() => {
    if (selectedVersion) {
      loadVersionDetails(selectedVersion.id);
    } else {
      setVersionDetails(null);
    }
  }, [selectedVersion, flowId]);

  const saveVersion = async () => {
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/versions/save?owner_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flow_id: flowId,
          flow_name: flowName,
          nodes: currentNodes,
          edges: currentEdges,
          message: saveMessage || 'Manual save',
          auto_save: false
        })
      });
      
      if (res.ok) {
        setSaveMessage('');
        loadVersions();
      }
    } catch (e) {
      console.error('Error saving version:', e);
    } finally {
      setSaving(false);
    }
  };

  const rollbackToVersion = async () => {
    if (!selectedVersion) return;
    
    try {
      setRollingBack(true);
      const res = await fetch(
        `${API_URL}/versions/${flowId}/rollback/${selectedVersion.id}?owner_id=${userId}`,
        { method: 'POST' }
      );
      
      if (res.ok) {
        const data = await res.json();
        onRollback(data.nodes, data.edges);
        loadVersions();
        onClose();
      }
    } catch (e) {
      console.error('Error rolling back:', e);
    } finally {
      setRollingBack(false);
    }
  };

  const compareVersions = async () => {
    if (!selectedVersion || !compareVersion) return;
    
    try {
      const res = await fetch(
        `${API_URL}/versions/${flowId}/diff/${selectedVersion.id}/${compareVersion.id}`
      );
      if (res.ok) {
        const data = await res.json();
        setDiffResult(data);
      }
    } catch (e) {
      console.error('Error comparing:', e);
    }
  };

  useEffect(() => {
    if (compareMode && selectedVersion && compareVersion) {
      compareVersions();
    } else {
      setDiffResult(null);
    }
  }, [selectedVersion, compareVersion, compareMode]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    
    if (diff < 60000) return 'Ora';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min fa`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} ore fa`;
    
    return d.toLocaleString('it-IT', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-[900px] max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <History className="w-6 h-6" />
            <h2 className="text-xl font-bold">Cronologia Versioni</h2>
            <span className="text-sm opacity-75">({flowName})</span>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="p-4 border-b flex items-center gap-4">
          <div className="flex-1 flex items-center gap-2">
            <input
              type="text"
              value={saveMessage}
              onChange={e => setSaveMessage(e.target.value)}
              placeholder="Descrizione modifica (opzionale)"
              className="flex-1 px-3 py-2 border rounded-lg"
            />
            <button
              onClick={saveVersion}
              disabled={saving}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Salva Versione
            </button>
          </div>
          
          <button
            onClick={() => {
              setCompareMode(!compareMode);
              setCompareVersion(null);
              setDiffResult(null);
            }}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
              compareMode ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <GitBranch className="w-4 h-4" />
            Confronta
          </button>
          
          <button 
            onClick={loadVersions}
            className="p-2 text-gray-500 hover:text-indigo-600"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Version List */}
          <div className="flex-1 overflow-auto">
            {versions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nessuna versione salvata</p>
                <p className="text-sm mt-2">Salva la prima versione del tuo flow</p>
              </div>
            ) : (
              <div className="divide-y">
                {versions.map((version, index) => (
                  <div
                    key={version.id}
                    onClick={() => {
                      if (compareMode && selectedVersion && selectedVersion.id !== version.id) {
                        setCompareVersion(version);
                      } else {
                        setSelectedVersion(version);
                      }
                    }}
                    className={`p-4 cursor-pointer transition-all ${
                      selectedVersion?.id === version.id 
                        ? 'bg-indigo-50 border-l-4 border-indigo-500' 
                        : compareVersion?.id === version.id
                        ? 'bg-green-50 border-l-4 border-green-500'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          index === 0 ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {version.version_number}
                        </div>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {version.message}
                            {version.auto_save && (
                              <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                                auto
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-3 mt-1">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(version.created_at)}
                            </span>
                            <span>{version.node_count} nodi</span>
                            <span>{version.edge_count} connessioni</span>
                          </div>
                          {version.diff_summary && (
                            <div className="text-xs text-indigo-600 mt-1 font-mono">
                              {version.diff_summary}
                            </div>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Version Preview / Diff */}
          {(selectedVersion && !compareMode) && (
            <div className="w-80 border-l bg-gray-50 p-4 overflow-auto">
              <h3 className="font-bold mb-4">Versione {selectedVersion.version_number}</h3>
              
              <div className="bg-white rounded-lg p-4 mb-4">
                <div className="text-sm space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Hash:</span>
                    <span className="font-mono text-xs">{selectedVersion.hash}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Nodi:</span>
                    <span className="font-bold">{selectedVersion.node_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Connessioni:</span>
                    <span className="font-bold">{selectedVersion.edge_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Data:</span>
                    <span className="text-xs">{new Date(selectedVersion.created_at).toLocaleString('it-IT')}</span>
                  </div>
                </div>
              </div>
              
              {versionDetails?.diff_from_previous && (
                <div className="bg-white rounded-lg p-4 mb-4">
                  <h4 className="font-bold text-sm mb-2">📊 Modifiche</h4>
                  <div className="text-sm space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-green-600">+{versionDetails.diff_from_previous.nodes.added}</span>
                      <span className="text-red-600">-{versionDetails.diff_from_previous.nodes.removed}</span>
                      <span className="text-blue-600">~{versionDetails.diff_from_previous.nodes.modified}</span>
                      <span className="text-gray-500">nodi</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-600">+{versionDetails.diff_from_previous.edges.added}</span>
                      <span className="text-red-600">-{versionDetails.diff_from_previous.edges.removed}</span>
                      <span className="text-gray-500">connessioni</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <button
                  onClick={rollbackToVersion}
                  disabled={rollingBack}
                  className="w-full py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {rollingBack ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <RotateCcw className="w-4 h-4" />
                  )}
                  Ripristina questa versione
                </button>
                
                <button
                  onClick={() => {
                    if (versionDetails) {
                      const blob = new Blob([JSON.stringify(versionDetails, null, 2)], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${flowName}-v${selectedVersion.version_number}.json`;
                      a.click();
                    }
                  }}
                  className="w-full py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Esporta versione
                </button>
              </div>
            </div>
          )}

          {/* Compare Mode */}
          {compareMode && diffResult && (
            <div className="w-96 border-l bg-gray-50 p-4 overflow-auto">
              <h3 className="font-bold mb-4">Confronto Versioni</h3>
              
              <div className="bg-white rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-indigo-600">v{diffResult.version_a.version_number}</span>
                  <span className="text-gray-400">vs</span>
                  <span className="font-bold text-green-600">v{diffResult.version_b.version_number}</span>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4">
                <h4 className="font-bold text-sm mb-3">📊 Differenze</h4>
                
                <div className="space-y-4">
                  <div>
                    <h5 className="text-sm font-medium mb-2">Nodi</h5>
                    <div className="text-sm space-y-1">
                      <div className="flex items-center gap-2 text-green-600">
                        <span className="font-bold">+{diffResult.diff.nodes.added}</span>
                        <span>aggiunti</span>
                      </div>
                      <div className="flex items-center gap-2 text-red-600">
                        <span className="font-bold">-{diffResult.diff.nodes.removed}</span>
                        <span>rimossi</span>
                      </div>
                      <div className="flex items-center gap-2 text-blue-600">
                        <span className="font-bold">~{diffResult.diff.nodes.modified}</span>
                        <span>modificati</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="text-sm font-medium mb-2">Connessioni</h5>
                    <div className="text-sm space-y-1">
                      <div className="flex items-center gap-2 text-green-600">
                        <span className="font-bold">+{diffResult.diff.edges.added}</span>
                        <span>aggiunte</span>
                      </div>
                      <div className="flex items-center gap-2 text-red-600">
                        <span className="font-bold">-{diffResult.diff.edges.removed}</span>
                        <span>rimosse</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {compareMode && !diffResult && (
            <div className="w-80 border-l bg-gray-50 p-4 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <GitBranch className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">Modalità Confronto</p>
                <p className="text-sm mt-2">
                  Seleziona due versioni<br/>da confrontare
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

